/**
 * ScriptTaskInspector — Phase 5a-7 + Phase 6c-4 Monaco upgrade.
 *
 * Edits inline-code execution task config: language + code body + timeout.
 *
 * Phase 6c-4 swaps the plain monospace textarea for @monaco-editor/react,
 * giving the operator real syntax highlighting, line numbers, bracket
 * matching, and auto-indent. Monaco is already a project dep (see
 * package.json:7); no new install required.
 *
 * Language mapping:
 *   • 'python'     → Monaco mode 'python'
 *   • 'javascript' → Monaco mode 'javascript'
 *   • 'bash'       → Monaco mode 'shell' (Monaco doesn't ship 'bash' but
 *                    its 'shell' tokenizer handles POSIX/bash close enough
 *                    for governance scripts)
 *
 * We lazy-load Monaco via React.lazy so the Inspector shell doesn't drag
 * the editor's ~3MB worker into every page render — only mounts when an
 * operator actually opens a script_task. The fallback shows the previous
 * monospace textarea so editing still works while the chunk loads.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React, { Suspense } from 'react';

const LANGUAGES = ['python', 'javascript', 'bash'];

// Monaco → which mode to feed it. 'bash' lands on 'shell' because Monaco
// doesn't bundle a bash tokenizer but the shell one is close enough.
const MONACO_LANG = {
  python:     'python',
  javascript: 'javascript',
  bash:       'shell',
};

// Lazy-load the editor. React.lazy + Suspense keeps the ~3MB worker out
// of the main Inspector bundle; only loaded when an operator opens a
// script_task. The fallback is the plain monospace textarea so editing
// still works during the lazy-load window.
const MonacoEditor = React.lazy(() => import('@monaco-editor/react'));

export default function ScriptTaskInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const language = LANGUAGES.includes(cfg.language) ? cfg.language : 'python';
  const code = cfg.code || '';
  const timeoutMs = cfg.timeoutMs || cfg.timeout || '';

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  const placeholder = language === 'python'
    ? '# input is the upstream task output\nresult = input["alert"]["severity"]\nreturn { "severity": result }'
    : language === 'bash'
      ? '# input is the upstream task output (as $INPUT_JSON)\necho "$INPUT_JSON" | jq .alert.severity'
      : '// input is the upstream task output\nconst result = input.alert.severity;\nreturn { severity: result };';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Language</span>
        <select
          value={language}
          onChange={(e) => setField('language', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Code</span>
        <div style={editorContainerStyle}>
          <Suspense fallback={
            // Fallback textarea — used while the Monaco chunk loads, or
            // as a permanent fallback if the lazy-load fails (offline /
            // CSP blocks the worker). Keeps editing usable in either case.
            <textarea
              value={code}
              onChange={(e) => setField('code', e.target.value)}
              placeholder={placeholder}
              disabled={readOnly}
              rows={14}
              style={fallbackCodeStyle}
              spellCheck={false}
            />
          }>
            <MonacoEditor
              height="280px"
              language={MONACO_LANG[language] || 'plaintext'}
              value={code}
              onChange={(value) => setField('code', value ?? '')}
              theme="vs"
              options={{
                readOnly,
                minimap: { enabled: false },
                lineNumbers: 'on',
                folding: true,
                wordWrap: 'on',
                fontSize: 12,
                fontFamily: 'SFMono-Regular, Menlo, Monaco, "Courier New", monospace',
                tabSize: 2,
                insertSpaces: true,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                renderWhitespace: 'selection',
                bracketPairColorization: { enabled: true },
                placeholder, // Monaco picks this up via the 'placeholder' option in some versions
              }}
            />
          </Suspense>
        </div>
        <span style={{ fontSize: 10, color: '#64748b' }}>
          Use <code style={codeChip}>input</code> for upstream output. Returned value flows downstream.
        </span>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Timeout (ms)</span>
        <input
          type="number"
          min="0"
          step="100"
          value={timeoutMs}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') setField('timeoutMs', undefined);
            else { const n = Number(v); if (Number.isFinite(n)) setField('timeoutMs', n); }
          }}
          placeholder="30000"
          disabled={readOnly}
          style={inputStyle}
        />
      </label>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '6px 8px',
  fontSize: 12, color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1', borderRadius: 4,
  outline: 'none', fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };

const editorContainerStyle = {
  width: '100%',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  overflow: 'hidden',
  background: '#ffffff',
};

const fallbackCodeStyle = {
  ...inputStyle,
  border: 'none',
  borderRadius: 0,
  fontFamily: 'SFMono-Regular, Menlo, Monaco, "Courier New", monospace',
  fontSize: 11, lineHeight: 1.5,
  tabSize: 2,
  minHeight: 280,
};

const codeChip = {
  padding: '0 4px',
  borderRadius: 3,
  background: '#f1f5f9',
  color: '#475569',
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  fontSize: 10,
};
